import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Circle,
  Flag,
  Calendar,
  Loader2,
  CheckCircle2,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { format, isBefore, isAfter, startOfDay, addDays } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Task, TaskPriority, TaskFilters } from '@/app/types/task';
import { taskService } from '@/app/services/taskService';
import { cn } from '@/lib/utils';

const priorityConfig: Record<TaskPriority, { color: string; bgColor: string }> = {
  [TaskPriority.HIGH]: { color: 'text-destructive', bgColor: 'bg-destructive/5' },
  [TaskPriority.MEDIUM]: { color: 'text-warning', bgColor: 'bg-warning/5' },
  [TaskPriority.LOW]: { color: 'text-primary', bgColor: 'bg-primary/5' },
};

interface TaskPanelProps {
  title: string;
  icon?: LucideIcon;
  queryKey: unknown[];
  fetchParams: TaskFilters & { limit?: number; sort_by?: string; sort_dir?: 'asc' | 'desc' };
  viewMoreHref: string;
  limit?: number;
  emptyText?: string;
  className?: string;
}

/**
 * Compact, actionable task list panel for dashboards.
 *
 * Each row supports one-click "mark complete" and click-to-open-detail (same window).
 * The header carries a "View more" deep-link into the pre-filtered task list.
 */
export default function TaskPanel({
  title,
  icon: Icon,
  queryKey,
  fetchParams,
  viewMoreHref,
  limit = 10,
  emptyText = 'Nothing here right now',
  className,
}: TaskPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => taskService.list({ limit, ...fetchParams }),
    refetchInterval: 60000,
  });

  const completeMutation = useMutation({
    mutationFn: (taskId: number) => taskService.markComplete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task marked as completed');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to complete task');
    },
  });

  const tasks = (data?.items ?? []).slice(0, limit);
  const total = data?.total ?? 0;

  const isOverdue = (task: Task) =>
    !!task.due_date && isBefore(new Date(task.due_date), startOfDay(new Date()));
  const isDueToday = (task: Task) => {
    if (!task.due_date) return false;
    const d = new Date(task.due_date);
    const today = startOfDay(new Date());
    return isAfter(d, today) && isBefore(d, addDays(today, 1));
  };

  const renderRow = (task: Task) => (
    <div
      key={task.id}
      className="flex items-start gap-2 p-2.5 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors group"
      onClick={() => navigate(`/tasks/detail/${task.id}`)}
    >
      <Button
        variant="ghost"
        size="sm"
        className="h-4 w-4 p-0 mt-0.5 flex-shrink-0"
        title="Mark complete"
        onClick={(e) => {
          e.stopPropagation();
          completeMutation.mutate(task.id);
        }}
      >
        <Circle className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
      </Button>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1">
          <span className="text-xs sm:text-sm font-medium line-clamp-2">{task.title}</span>
          <Badge
            variant="outline"
            className={cn(
              'text-xs flex-shrink-0 px-1 py-0 h-5',
              priorityConfig[task.priority]?.bgColor,
              priorityConfig[task.priority]?.color
            )}
          >
            <Flag className="h-2.5 w-2.5" />
          </Badge>
        </div>
        {task.party?.name && (
          <div className="text-xs text-muted-foreground line-clamp-1">{task.party.name}</div>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
          {task.task_category && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 h-4">
              {task.task_category}
            </Badge>
          )}
          {task.due_date && (
            <div
              className={cn(
                'flex items-center gap-0.5',
                isOverdue(task) && 'text-destructive font-medium',
                isDueToday(task) && 'text-warning font-medium'
              )}
            >
              <Calendar className="h-2.5 w-2.5" />
              <span className="text-xs">
                {isDueToday(task) ? 'Today' : format(new Date(task.due_date), 'MMM dd')}
              </span>
            </div>
          )}
          {task.assigned_to && (
            <span className="truncate max-w-[90px] text-xs">
              {task.assigned_to.full_name || task.assigned_to.username}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4" />}
            {title}
            {total > 0 && (
              <Badge variant="outline" className="text-xs">
                {total}
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => navigate(viewMoreHref)}
          >
            View more
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Failed to load tasks</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{emptyText}</p>
          </div>
        ) : (
          <div className="space-y-2">{tasks.map(renderRow)}</div>
        )}
      </CardContent>
    </Card>
  );
}
